import type React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { DynamicMap } from './DynamicMap';
import { CommuteTimeCards } from './Commute';
import { useNeighborhoodContextSafe } from './context';
import { useCommuteListingSync } from './hooks';
import { isValidCoordinate } from './utils/coordinates';
import {
  commuteSection,
  fallbackContainer,
  fallbackIcon,
  fallbackSubtext,
  fallbackText,
  mapContainer,
  mapSection,
  moduleContainer,
} from './neighborhoodModule.styles';

/**
 * NeighborhoodContent — map preview and commute cards for the neighborhood module.
 *
 * Rendered inside NeighborhoodModule (which provides NeighborhoodProvider context).
 * Fetches listing data from Redux.
 */
export const NeighborhoodContent = () => {
  const contextValue = useNeighborhoodContextSafe();

  // Fetch data directly from Redux state
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  // Extract listing data with proper types
  const listing = currentListing
    ? {
        geo: currentListing.geo,
        displayName: currentListing.displayName,
        address: currentListing.address
          ? `${currentListing.address.street}, ${currentListing.address.city}, ${currentListing.address.state}`
          : '',
      }
    : undefined;

  const address = currentListing?.address
    ? `${currentListing.address.street}, ${currentListing.address.city}, ${currentListing.address.state}`
    : undefined;

  // Recalculate commute times when the active listing changes (HP-7574).
  useCommuteListingSync({ id: currentListing?.maloneLotIdEncoded, geo: currentListing?.geo });

  // Extract context data safely, with fallbacks for when no provider exists
  const commute = contextValue?.state.commute || {
    destination: null,
    times: [],
    selectedMode: undefined,
    status: 'idle' as const,
  };
  const actions = contextValue?.actions;

  const hasValidListing = listing?.geo?.lat !== undefined && listing?.geo?.lon !== undefined;
  const hasValidCoordinates = hasValidListing && isValidCoordinate(listing.geo.lat, listing.geo.lon);

  // Fallback content when location data is not available
  const FallbackContent = () => (
    <div className={fallbackContainer} aria-label="Location information not available">
      <div className={fallbackIcon}>📍</div>
      <div className={fallbackText}>Location not available</div>
      <div className={fallbackSubtext}>{address ? `Address: ${address}` : 'No address information'}</div>
    </div>
  );

  return (
    <section
      className={moduleContainer}
      data-testid="neighborhood-module"
      role="region"
      aria-label="Neighborhood information"
    >
      <div className={mapSection}>
        <div className={mapContainer}>
          {hasValidCoordinates ? (
            <DynamicMap
              lat={listing.geo.lat}
              lon={listing.geo.lon}
              address={listing.address || address || ''}
              height={240}
              zoom={15}
              commuteDestination={commute.destination}
              selectedCommuteMode={commute.selectedMode}
              allowExpandedBounds={!!commute.destination}
              aria-label={`Map showing location of ${listing.displayName || address || 'property'}`}
            />
          ) : (
            <FallbackContent />
          )}
        </div>

        {/* Display commute time cards while a destination is set — renders a
            loading state during recalc so the cards don't disappear when the
            active listing changes. */}
        {(commute.status === 'calculating' || commute.status === 'success') && commute.destination && actions && (
          <div className={commuteSection} aria-label="Commute information">
            <CommuteTimeCards
              destination={commute.destination}
              commuteTimes={commute.times}
              isLoading={commute.status === 'calculating'}
              selectedMode={commute.selectedMode}
              onModeSelect={actions.setSelectedMode}
              onEdit={actions.clearCommute}
              data-testid="neighborhood-module-commute-cards"
            />
          </div>
        )}
      </div>
    </section>
  );
};
